import type { AppRouter } from '@app/api'
import { type PublicScript, type Said as SaidText, say } from '@app/shared'
import type { inferRouterOutputs } from '@trpc/server'
import {
  type CSSProperties,
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { Said } from '../ui/Said'
import { Time } from './HomeTime'
import { type Cluster, clusterLabels } from './today-band-layout'
import './today-band.css'

type Band = NonNullable<
  inferRouterOutputs<AppRouter>['occasion']['leading']['band']
>
type Entry = Band['entries'][number]

/**
 * The blocks the labels are gathered into once measured, and how deep the
 * deepest is — or that the line cannot hold them.
 */
type Layout =
  | { fits: true; clusters: Cluster[]; reach: number; nowStart: number }
  | { fits: false }

const ms = (iso: string) => Date.parse(iso)

/**
 * Today's zmanim as one line, from the first the shul ticked to the last, with
 * where now falls on it.
 *
 * Everything here is the server's: the times (each resolved where the sheet and
 * the zmanim chart resolve theirs), the shul's date, and the moment it was
 * drawn for, in the shul's own zone. Positions are differences between
 * instants, which no viewer's zone can move — so a reader in another country
 * sees the shul's day, and "now" at the shul's hour.
 *
 * **The labels are one row of blocks beneath the line, gathered only as far
 * as the room needs.** With room, every zman stands alone under its tick; with
 * less, zmanim that are the same thing in the day share a block (the sun
 * coming up, the morning's deadlines, nightfall); with less still, the parts
 * of the day. Below that the band is a list down a line — which is also what a
 * phone gets. Measured against the band's own width, never the window's.
 * Pointing at a name lights its tick, and pointing at a tick lights its name,
 * which is what ties a name in a block to its own place in the day.
 *
 * **It runs the way its names read.** Where the shul names zmanim in Hebrew
 * the line starts at the right, and every position is measured from its start
 * so the one placement serves either direction.
 */
export function TodayBand({
  band,
  place,
  script,
}: {
  band: Band
  place: string | null
  /** Which script the shul's site names zmanim in. */
  script: PublicScript
}) {
  const nameOf = (e: Entry) => say(script, e.labelEn, e.labelHe)
  const dir = script === 'he' ? 'rtl' : undefined
  const entries = band.entries
  const first = ms(entries[0]?.times[0]?.at ?? band.now.at)
  const last = Math.max(...entries.flatMap((e) => e.times.map((t) => ms(t.at))))
  const span = Math.max(last - first, 1)
  const along = (iso: string) =>
    Math.min(1, Math.max(0, (ms(iso) - first) / span))
  const now = ms(band.now.at)
  const nowAt = along(band.now.at)
  const passed = (iso: string) => ms(iso) <= now
  const over = (e: Entry) => e.times.every((t) => passed(t.at))

  // A label stands over the middle of its ticks — one tick per opinion.
  const labeled = entries
    .map((e) => ({
      id: e.key,
      x: e.times.reduce((sum, t) => sum + along(t.at), 0) / e.times.length,
      group: e.group,
      part: e.part,
    }))
    .sort((a, b) => a.x - b.x)

  /* The zman the reader is pointing at, by its name or by its tick: both
     light together, which is how a name in a block is tied to its own place
     in the day. */
  const [hot, setHot] = useState<string | null>(null)

  const byKey = new Map(entries.map((e) => [e.key, e]))

  /* Read by `measure` through a ref, so the measurement stays one function for
     the band's life and the observer is not re-attached on every render. */
  const items = useRef(labeled)
  items.current = labeled

  const track = useRef<HTMLDivElement>(null)
  const labels = useRef(new Map<string, HTMLElement>())
  const nowLabel = useRef<HTMLSpanElement>(null)
  const nowAtRef = useRef(nowAt)
  nowAtRef.current = nowAt
  const [layout, setLayout] = useState<Layout | null>(null)

  const measure = useCallback(() => {
    const el = track.current
    if (!el) return
    /* The room kept between two blocks, and between two labels stacked in
       one, are the track's own gaps. */
    const style = getComputedStyle(el)
    const gap = Number.parseFloat(style.columnGap || '0') || 0
    const rowGap = Number.parseFloat(style.rowGap || '0') || 0
    const placed = clusterLabels(
      items.current.map((item) => {
        const box = labels.current.get(item.id)
        return {
          ...item,
          width: box?.offsetWidth ?? 0,
          height: box?.offsetHeight ?? 0,
        }
      }),
      el.clientWidth,
      gap,
      rowGap,
    )
    /* Now's name stands centered over its mark, kept inside the line: from
       the last zman to midnight, and from midnight to the first, the mark
       sits at an end and the name would otherwise hang off it. */
    const nowWidth = nowLabel.current?.offsetWidth ?? 0
    const nowStart = Math.min(
      Math.max(nowAtRef.current * el.clientWidth - nowWidth / 2, 0),
      Math.max(el.clientWidth - nowWidth, 0),
    )
    setLayout((prev) => {
      if (!placed) return prev && !prev.fits ? prev : { fits: false }
      return prev?.fits &&
        prev.reach === placed.reach &&
        prev.nowStart === nowStart &&
        JSON.stringify(prev.clusters) === JSON.stringify(placed.clusters)
        ? prev
        : { fits: true, ...placed, nowStart }
    })
  }, [])

  /* Before paint on every commit, so a band whose entries changed is never
     drawn with the last one's placement. The observer covers what no commit
     describes: the band's width changing, and a label's own width changing
     when the page's faces arrive after the first measurement. It is attached
     again whenever the blocks are regrouped, since regrouping draws the labels
     afresh; and a name being pointed at is let go then, since the element the
     pointer was over is gone. */
  useLayoutEffect(measure)
  const grouping = layout?.fits
    ? layout.clusters.map((c) => c.ids.join(' ')).join('|')
    : String(layout?.fits)
  useLayoutEffect(() => {
    const el = track.current
    if (!el || grouping === undefined) return
    setHot(null)
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    for (const label of labels.current.values()) observer.observe(label)
    if (nowLabel.current) observer.observe(nowLabel.current)
    let live = true
    document.fonts?.ready.then(() => {
      if (live) measure()
    })
    return () => {
      live = false
      observer.disconnect()
    }
  }, [measure, grouping])

  const fits = layout?.fits === true
  /* Until the labels are measured each stands in a block of its own, unseen,
     so its size can be read. */
  const clusters: Cluster[] = layout?.fits
    ? layout.clusters
    : labeled.map((item) => ({ ids: [item.id], start: 0 }))
  const keep = (id: string) => (el: HTMLElement | null) => {
    if (el) labels.current.set(id, el)
    else labels.current.delete(id)
  }

  const nowIndex = entries.findIndex((e) => !over(e))
  const nowRowAt = nowIndex === -1 ? entries.length : nowIndex

  return (
    <section className="today" aria-labelledby="today-h">
      <div className="home__measure today__inner">
        <h2 id="today-h" className="home__h2">
          {place ? `Today in ${place}` : 'Today'}
        </h2>

        <div className="today__body" dir={dir}>
          {/* The line. Laid out even while the list stands in for it, so its
            labels can still be measured against the room the band has. */}
          <div
            ref={track}
            className="today__track"
            data-state={layout ? (fits ? 'line' : 'list') : 'measuring'}
            aria-hidden={fits ? undefined : true}
            style={
              {
                '--reach': `${layout?.fits ? layout.reach : 0}px`,
              } as CSSProperties
            }
          >
            <span className="today__rail" aria-hidden />
            <span
              className="today__passed"
              aria-hidden
              style={{ '--x': nowAt } as CSSProperties}
            />
            {entries.flatMap((e) =>
              e.times.map((t) => (
                <span
                  key={`${e.key}:${t.key}`}
                  className="today__tick"
                  aria-hidden
                  data-passed={passed(t.at) ? '' : undefined}
                  data-anchor={e.anchor ? '' : undefined}
                  data-hot={hot === e.key ? '' : undefined}
                  onPointerEnter={() => setHot(e.key)}
                  onPointerLeave={() => setHot(null)}
                  style={{ '--x': along(t.at) } as CSSProperties}
                />
              )),
            )}
            <span
              className="today__dot"
              aria-hidden
              style={{ '--x': nowAt } as CSSProperties}
            />
            {/* Now is the reader's position, not a zman: named over its own
                mark, apart from the zmanim beneath the line. */}
            <span
              ref={nowLabel}
              className="today__now"
              style={{ insetInlineStart: layout?.fits ? layout.nowStart : 0 }}
            >
              Now{' '}
              <span className="today__clock">
                <Time value={band.now.time} />
              </span>
            </span>
            {/* The blocks in time order, as a reader meets the day. Pointing
                at a name lights its tick. */}
            {clusters.map((cluster) => (
              <span
                key={cluster.ids.join(' ')}
                className="today__block"
                style={{ insetInlineStart: cluster.start }}
              >
                {cluster.ids.map((id) => {
                  const e = byKey.get(id)
                  return e ? (
                    <span
                      key={e.key}
                      ref={keep(e.key)}
                      className="today__label"
                      data-anchor={e.anchor ? '' : undefined}
                      data-hot={hot === e.key ? '' : undefined}
                      onPointerEnter={() => setHot(e.key)}
                      onPointerLeave={() => setHot(null)}
                    >
                      <Label name={nameOf(e)} entry={e} />
                    </span>
                  ) : null
                })}
              </span>
            ))}
          </div>

          {layout && !fits ? (
            <ol className="today__list">
              {entries.map((e, i) => (
                <Fragment key={e.key}>
                  {i === nowRowAt ? <NowRow time={band.now.time} /> : null}
                  <li
                    className="today__row"
                    data-passed={over(e) ? '' : undefined}
                    data-anchor={e.anchor ? '' : undefined}
                  >
                    <span className="today__row-name">
                      <Said said={nameOf(e)} heClassName="today__he" />
                    </span>
                    <Times entry={e} />
                  </li>
                </Fragment>
              ))}
              {nowRowAt === entries.length ? (
                <NowRow time={band.now.time} />
              ) : null}
            </ol>
          ) : null}
        </div>
      </div>
    </section>
  )
}

/**
 * A label on the line: the name the shul reads, with the time beside it, and
 * the Hebrew under it where the shul shows both.
 */
function Label({ name, entry }: { name: SaidText; entry: Entry }) {
  return (
    <>
      <span className="today__name">
        <Said said={{ en: name.en, he: name.en ? null : name.he }} />{' '}
        <Times entry={entry} />
      </span>
      {name.en && name.he ? (
        <span className="today__he said-pair" lang="he" dir="rtl">
          {name.he}
        </span>
      ) : null}
    </>
  )
}

/** An entry's time, or each opinion's side by side as the sheet prints them. */
function Times({ entry }: { entry: Entry }) {
  return (
    <span className="today__time">
      {entry.times.map((t, i) => (
        <span key={t.key}>
          {i ? ' / ' : ''}
          <Time value={t.time} />
        </span>
      ))}
    </span>
  )
}

function NowRow({ time }: { time: string }) {
  return (
    <li className="today__row today__row--now">
      Now{' '}
      <span className="today__clock">
        <Time value={time} />
      </span>
    </li>
  )
}
